import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Phone, Clock, MapPin, ArrowUp } from "lucide-react";
import {
  CLINIC_NAME,
  ADDRESS,
  HOURS,
  PHONE_DISPLAY,
  PHONE_TEL,
  WHATSAPP_URL,
  MAPS_DIRECTIONS,
} from "@/lib/site";
import { Reveal } from "@/components/site/Reveal";
import { WhatsappButton } from "@/components/site/WhatsappButton";

export const Footer = () => {
  const scrollToTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer
      id="contato"
      data-testid="footer-cta"
      className="relative bg-clinic-ink text-clinic-bg overflow-hidden"
    >
      {/* Big final CTA */}
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-sans text-xs uppercase tracking-[0.35em] text-clinic-pink font-semibold mb-5">
                Agende sua consulta
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tight font-medium">
                Pronta para cuidar
                <br />
                de <span className="italic text-clinic-pink font-normal">você mesma?</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 font-sans text-base text-clinic-bg/75 max-w-lg leading-relaxed">
                Fale conosco pelo WhatsApp e agende um horário personalizado. Será um prazer
                receber você no nosso espaço acolhedor na Clínica Lília Targa.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Reveal delay={0.3}>
              <WhatsappButton
                label="Agendar pelo WhatsApp"
                size="lg"
                testId="footer-whatsapp-cta"
              />
            </Reveal>
          </div>
        </div>
      </div>

      {/* Info grid */}
      <div className="mx-auto max-w-7xl px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
          <p className="font-serif text-2xl md:text-3xl leading-none font-medium">{CLINIC_NAME}</p>
          <p className="mt-2.5 font-sans text-xs tracking-[0.25em] uppercase text-clinic-pink/80">
            Saúde & Beleza · Jundiaí-SP
          </p>
          <p className="mt-4 font-sans text-xs text-clinic-bg/60 leading-relaxed max-w-xs">
            Tratamentos faciais e corporais personalizados para realçar a sua beleza natural.
          </p>
        </div>

        <div className="flex gap-3">
          <MapPin className="h-5 w-5 shrink-0 text-clinic-pink mt-0.5" strokeWidth={1.75} />
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-bg/50 font-medium">
              Endereço
            </p>
            <a
              href={MAPS_DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 block font-sans text-sm text-clinic-bg/85 hover:text-clinic-pink transition-colors leading-relaxed"
            >
              {ADDRESS}
            </a>
          </div>
        </div>

        <div className="flex gap-3">
          <Phone className="h-5 w-5 shrink-0 text-clinic-pink mt-0.5" strokeWidth={1.75} />
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-bg/50 font-medium">
              Contato
            </p>
            <a
              href={PHONE_TEL}
              data-testid="footer-phone-link"
              className="mt-1.5 block font-sans text-sm font-medium text-clinic-bg/90 hover:text-clinic-pink transition-colors"
            >
              {PHONE_DISPLAY}
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 font-sans text-xs text-clinic-pink hover:underline"
            >
              <FaWhatsapp className="h-3.5 w-3.5" /> Chamar no WhatsApp
            </a>
          </div>
        </div>

        <div className="flex gap-3">
          <Clock className="h-5 w-5 shrink-0 text-clinic-pink mt-0.5" strokeWidth={1.75} />
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-clinic-bg/50 font-medium">
              Horário
            </p>
            <p className="mt-1.5 font-sans text-sm text-clinic-bg/85 font-medium">{HOURS}</p>
          </div>
        </div>
      </div>

      {/* Bottom bar with Back-to-Top */}
      <div className="border-t border-white/10 bg-black/10">
        <div className="mx-auto max-w-7xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-sans text-xs text-clinic-bg/40 text-center sm:text-left">
            © {new Date().getFullYear()} {CLINIC_NAME}. Todos os direitos reservados.
          </p>

          <button
            onClick={scrollToTop}
            aria-label="Voltar ao topo"
            className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-sans text-xs text-clinic-bg/60 hover:bg-white/10 hover:text-clinic-bg transition-all active:scale-95"
          >
            <span>Voltar ao topo</span>
            <ArrowUp className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 text-clinic-pink" />
          </button>
        </div>
      </div>
    </footer>
  );
};

